// Loading inspections, photos and damage marks for reports, recording repairs, and saving the marks placed in the
// admin inspection form.
import { supabase } from "@/lib/supabase";
import type { DamageSighting } from "@/lib/damageRegister";

export const BUCKET_VEHICLE_INSPECTIONS = "vehicle-inspections";

export interface InspectionRow {
  id: string;
  vehicle_number: string;
  driver_name: string | null;
  created_at: string;
  completed_at: string | null;
  inspection_type: string | null;
  overall_status: string | null;
  notes: string | null;
  progress_pct: number | null;
  odometer_km: number | null;
}

export interface PhotoRow {
  id: string;
  inspection_id: string;
  category: string | null;
  photo_type: string;
  storage_path: string;
}

export interface MarkerRow {
  id: string;
  inspection_id: string | null;
  photo_id: string;
  x_percent: number;
  y_percent: number;
  damage_type: string;
  severity: string;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  repaired_on: string | null;
  repaired_by: string | null;
  repair_notes: string | null;
}

/** A checklist answer that was not OK (the only ones a report needs). */
export interface IssueItemRow {
  inspection_id: string;
  category: string;
  label: string;
  status: string;
  note: string | null;
}

export interface DamageData {
  /** Finished inspections (and old ones that have marks), newest first. In-progress drafts are left out. */
  inspections: InspectionRow[];
  photos: PhotoRow[];
  markers: MarkerRow[];
  issues: IssueItemRow[];
  /** Every mark joined to its photo and inspection. */
  sightings: DamageSighting[];
}

export const photoUrl = (storagePath: string): string =>
  storagePath ? supabase.storage.from(BUCKET_VEHICLE_INSPECTIONS).getPublicUrl(storagePath).data?.publicUrl ?? "" : "";

const CHUNK = 100;

/** Runs `select ... where column in (ids)` in chunks, so a long list never overflows the request URL. */
async function inChunks<T>(table: string, select: string, column: string, ids: string[], extra?: (q: any) => any): Promise<T[]> {
  const out: T[] = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    let q = supabase.from(table).select(select).in(column, ids.slice(i, i + CHUNK));
    if (extra) q = extra(q);
    const { data, error } = await q;
    if (error) throw error;
    out.push(...((data ?? []) as T[]));
  }
  return out;
}

const INSPECTION_COLUMNS = "id, vehicle_number, driver_name, created_at, completed_at, inspection_type, overall_status, notes, progress_pct, odometer_km";

/**
 * Everything needed to report on damage and checks.
 * @param vehicle one vehicle, or null for the whole fleet
 * @param withPhotos load every photo of every inspection (for the printable report); otherwise only the photos that carry marks
 */
export async function loadDamageData(opts: { vehicle?: string | null; withPhotos?: boolean } = {}): Promise<DamageData> {
  const { vehicle = null, withPhotos = false } = opts;

  let inspections: InspectionRow[];
  let markers: MarkerRow[];

  if (vehicle) {
    const { data, error } = await supabase
      .from("vehicle_inspections")
      .select(INSPECTION_COLUMNS)
      .eq("vehicle_number", vehicle)
      .order("created_at", { ascending: false })
      .limit(2000);
    if (error) throw error;
    inspections = (data ?? []) as InspectionRow[];
    markers = await inChunks<MarkerRow>("vehicle_inspection_damage_markers", "*", "inspection_id", inspections.map((i) => i.id));
  } else {
    const { data: markerData, error: markerErr } = await supabase.from("vehicle_inspection_damage_markers").select("*").limit(20000);
    if (markerErr) throw markerErr;
    markers = (markerData ?? []) as MarkerRow[];

    const { data: doneData, error: doneErr } = await supabase
      .from("vehicle_inspections")
      .select(INSPECTION_COLUMNS)
      .not("completed_at", "is", null)
      .order("created_at", { ascending: false })
      .limit(5000);
    if (doneErr) throw doneErr;
    inspections = (doneData ?? []) as InspectionRow[];

    // Older inspections that have marks but were never stamped complete.
    const have = new Set(inspections.map((i) => i.id));
    const missing = [...new Set(markers.map((m) => m.inspection_id).filter((id): id is string => !!id && !have.has(id)))];
    if (missing.length) inspections.push(...(await inChunks<InspectionRow>("vehicle_inspections", INSPECTION_COLUMNS, "id", missing)));
  }

  // A draft that is still being worked on (has progress saved, not finished) is not part of the record yet.
  const markedInspectionIds = new Set(markers.map((m) => m.inspection_id));
  inspections = inspections
    .filter((i) => i.completed_at != null || (i.progress_pct == null && markedInspectionIds.has(i.id)))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const reportable = new Set(inspections.map((i) => i.id));
  markers = markers.filter((m) => m.inspection_id && reportable.has(m.inspection_id));

  const photos = withPhotos
    ? await inChunks<PhotoRow>("vehicle_inspection_photos", "id, inspection_id, category, photo_type, storage_path", "inspection_id", [...reportable])
    : await inChunks<PhotoRow>("vehicle_inspection_photos", "id, inspection_id, category, photo_type, storage_path", "id", [...new Set(markers.map((m) => m.photo_id))]);

  const issues = vehicle
    ? await inChunks<IssueItemRow>(
        "vehicle_inspection_items",
        "inspection_id, category, label, status, note",
        "inspection_id",
        [...reportable],
        (q) => q.neq("status", "OK")
      )
    : [];

  const photoById = new Map(photos.map((p) => [p.id, p]));
  const inspectionById = new Map(inspections.map((i) => [i.id, i]));
  const sightings: DamageSighting[] = [];
  for (const m of markers) {
    const photo = photoById.get(m.photo_id);
    const insp = m.inspection_id ? inspectionById.get(m.inspection_id) : undefined;
    if (!photo || !insp) continue;
    sightings.push({
      id: m.id,
      inspection_id: insp.id,
      inspection_at: insp.created_at,
      inspection_type: insp.inspection_type,
      inspected_by: insp.driver_name ?? "",
      photo_id: m.photo_id,
      photo_type: photo.photo_type,
      storage_path: photo.storage_path,
      x_percent: Number(m.x_percent),
      y_percent: Number(m.y_percent),
      damage_type: m.damage_type,
      severity: m.severity,
      notes: m.notes,
      repaired_on: m.repaired_on,
      repaired_by: m.repaired_by,
      repair_notes: m.repair_notes,
    });
  }

  return { inspections, photos, markers, issues, sightings };
}

// ---------------------------------------------------------------------------
// Repairs
// ---------------------------------------------------------------------------

/** Records that a piece of damage was repaired: stamped on every mark that makes it up. */
export async function markDamageRepaired(markerIds: string[], repairedOn: string, repairedBy: string, notes?: string): Promise<void> {
  if (!markerIds.length) return;
  const { error } = await supabase
    .from("vehicle_inspection_damage_markers")
    .update({ repaired_on: repairedOn, repaired_by: repairedBy.trim() || null, repair_notes: notes?.trim() || null })
    .in("id", markerIds);
  if (error) throw error;
}

export async function undoDamageRepair(markerIds: string[]): Promise<void> {
  if (!markerIds.length) return;
  const { error } = await supabase
    .from("vehicle_inspection_damage_markers")
    .update({ repaired_on: null, repaired_by: null, repair_notes: null })
    .in("id", markerIds);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Marks placed in the admin inspection form
// ---------------------------------------------------------------------------

/** The shape the marker overlay uses. */
export interface FormMarker {
  id?: string;
  x_percent: number;
  y_percent: number;
  damage_type: string;
  severity: "Minor" | "Moderate" | "Severe";
  notes: string;
}

export const markerContentKey = (m: FormMarker): string =>
  JSON.stringify([Number(m.x_percent), Number(m.y_percent), m.damage_type, m.severity, (m.notes ?? "").trim()]);

/** Marks already saved against these photos, grouped by photo id. */
export async function loadMarkersForPhotos(photoIds: string[]): Promise<Record<string, FormMarker[]>> {
  const rows = await inChunks<MarkerRow>("vehicle_inspection_damage_markers", "*", "photo_id", photoIds);
  const out: Record<string, FormMarker[]> = {};
  for (const r of rows) {
    (out[r.photo_id] ||= []).push({
      id: r.id,
      x_percent: Number(r.x_percent),
      y_percent: Number(r.y_percent),
      damage_type: r.damage_type,
      severity: (r.severity as FormMarker["severity"]) || "Minor",
      notes: r.notes ?? "",
    });
  }
  return out;
}

/**
 * Brings the saved marks for one photo in line with what's on screen: removed ones are deleted, changed ones updated,
 * new ones inserted. Returns the on-screen list with database ids filled in.
 */
export async function syncMarkersForPhoto(args: {
  photoId: string;
  inspectionId: string;
  current: FormMarker[];
  synced: FormMarker[];
  createdBy: string;
}): Promise<FormMarker[]> {
  const { photoId, inspectionId, current, synced, createdBy } = args;
  const currentIds = new Set(current.filter((m) => m.id).map((m) => m.id as string));

  const toDelete = synced.filter((m) => m.id && !currentIds.has(m.id)).map((m) => m.id as string);
  if (toDelete.length) {
    const { error } = await supabase.from("vehicle_inspection_damage_markers").delete().in("id", toDelete);
    if (error) throw error;
  }

  const syncedById = new Map(synced.filter((m) => m.id).map((m) => [m.id as string, m]));
  for (const m of current) {
    if (!m.id) continue;
    const before = syncedById.get(m.id);
    if (before && markerContentKey(before) === markerContentKey(m)) continue;
    const { error } = await supabase
      .from("vehicle_inspection_damage_markers")
      .update({ x_percent: m.x_percent, y_percent: m.y_percent, damage_type: m.damage_type, severity: m.severity, notes: m.notes?.trim() || null })
      .eq("id", m.id);
    if (error) throw error;
  }

  const fresh = current.filter((m) => !m.id);
  let inserted: { id: string }[] = [];
  if (fresh.length) {
    const { data, error } = await supabase
      .from("vehicle_inspection_damage_markers")
      .insert(
        fresh.map((m) => ({
          photo_id: photoId,
          inspection_id: inspectionId,
          x_percent: m.x_percent,
          y_percent: m.y_percent,
          damage_type: m.damage_type,
          severity: m.severity,
          notes: m.notes?.trim() || null,
          created_by: createdBy || null,
        }))
      )
      .select("id");
    if (error) throw error;
    inserted = (data ?? []) as { id: string }[];
  }

  let next = 0;
  return current.map((m) => (m.id ? m : inserted[next] ? { ...m, id: inserted[next++].id } : m));
}
